import { useCallback, useContext, useMemo } from 'react';
import { AuthContext } from '@/contexts/AuthContext';
import { useTour } from './TourContext';
import { TourKeys } from './tour-keys';
import {
  getToolBuilderStepIndex,
  isLastToolBuilderStep,
  LAST_STEP_INDEX,
  recordToolBuilderStepSeen,
} from './toolBuilderTourProgress';

/**
 * Closing a Tool-builder step. `dismissStep` keeps the tour alive: an intermediate coachmark only
 * records how far the user got, so the remaining ones still fire, and the last one completes the
 * tour. `abandonTour` is the coachmark X — it retires every step still owed.
 */
export function useToolBuilderStepDismiss() {
  const { currentUser } = useContext(AuthContext);
  const { endTour, goToStep, quitTour, stopTour } = useTour();
  const userId = currentUser?.uid;

  const dismissStep = useCallback(
    (stepIndex: number) => {
      const progress = recordToolBuilderStepSeen(userId, stepIndex);
      if (isLastToolBuilderStep(stepIndex)) {
        // Inputs is no longer last: dismissing it records progress so the preview coachmark can open.
        quitTour(TourKeys.ToolBuilder, 'completed');
        return;
      }
      goToStep(progress);
      stopTour();
    },
    [goToStep, quitTour, stopTour, userId],
  );

  const abandonTour = useCallback(() => {
    recordToolBuilderStepSeen(userId, LAST_STEP_INDEX);
    endTour();
  }, [endTour, userId]);

  return useMemo(() => ({ abandonTour, dismissStep }), [abandonTour, dismissStep]);
}

/**
 * Closes a Tool-builder step the way its "Got it" does, when the user takes the action it points at
 * (+ on the Outputs coachmark). Does nothing while another step, or no tour, is showing.
 */
export function useDismissToolBuilderStepOnAction(stepId: string) {
  const { activeTour, currentStep } = useTour();
  const { dismissStep } = useToolBuilderStepDismiss();

  return useCallback(() => {
    if (activeTour === TourKeys.ToolBuilder && currentStep === getToolBuilderStepIndex(stepId)) {
      dismissStep(currentStep);
    }
  }, [activeTour, currentStep, dismissStep, stepId]);
}
